// A single cache dossier card — the full two-stage flow (buy hints, name the
// place / buy the pin, then claim on site with the code + selfie). Rendered
// inside the tactical map's bottom sheet.
import { el, toast } from "../lib/dom";
import { db } from "../lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import {
  buyHint, revealCacheLocation, submitCacheAnswer,
  type Cache, type CacheProgress,
} from "../lib/data";
import { compressImage, uploadSelfie } from "../lib/upload";
import { noirProof } from "../lib/avatar-filter";

function stars(tier: number): HTMLElement {
  const t = Math.max(0, Math.min(5, tier));
  return el("span.stars", {}, "★".repeat(t) + "☆".repeat(5 - t));
}

export function cacheCard(
  c: Cache,
  prog: CacheProgress,
  ctx: { uid: string; codename: () => string },
): HTMLElement {
  const claimed = prog.status === "claimed";
  const revealed = !!prog.locationRevealed;
  const isSupply = c.kind === "powerup";
  const card = el("div.card", { style: "margin:0" },
    el("span.card__tag", isSupply ? { style: "color:var(--green);border-color:var(--green)" } : {}, c.name),
    isSupply ? el("span", { style: "font-size:15px" }, "🍺 SUPPLY DROP") : stars(c.powerTier),
  );

  // A cache another agent already reached: surface it as taken up top, with their
  // claim selfie as proof. (Guns can still be claimed by others — this is intel,
  // not a lock — so we don't touch the flow below.)
  const claimedByOther = !!c.firstClaimedBy && c.firstClaimedBy !== ctx.uid && !claimed;
  if (claimedByOther) {
    const proof = c.claimPhotos?.[c.firstClaimedBy!];
    card.append(el("div", {
      style: "display:inline-flex;align-items:center;gap:6px;margin:8px 0 4px;padding:3px 10px;" +
        "border:1px solid var(--cream-dim);border-radius:999px;color:var(--cream-dim);" +
        "font-family:var(--mono);font-size:11px;letter-spacing:.1em;text-transform:uppercase",
    }, isSupply ? "◆ Stash secured by another agent" : "◆ Gun claimed by another agent"));
    if (typeof proof === "string" && proof.startsWith("http")) {
      card.append(el("div", { style: "margin:2px 0 4px" },
        el("span.tiny.dim", {}, "CLAIM PROOF"),
        el("img", {
          src: proof,
          style: "display:block;width:100%;border-radius:6px;margin-top:4px;" +
            "border:1px solid var(--cream-dim);filter:grayscale(0.35) contrast(1.05)",
        }),
      ));
    }
  }

  // The riddle only matters while you're still hunting the spot. Once the pin is
  // revealed, it's dead weight — hide it so the location + photo take the stage.
  if (!revealed) card.append(el("p", {}, c.riddlePrompt));

  // ---- tiered hints (only while still finding the location) ----
  if (!claimed && !revealed) {
    const hints = el("div.tiny.amber", {},
      ...(prog.hintTexts ?? []).map((t) => el("div", {}, `▸ ${t}`)));
    const hintBtn = el("button.btn.btn--ghost.btn--sm", {}, `Buy hint (${c.hintCost} ◆)`) as HTMLButtonElement;
    hintBtn.addEventListener("click", async () => {
      hintBtn.disabled = true;
      try {
        const res = await buyHint({ cacheId: c.id });
        if (res.noMoreHints) {
          hintBtn.textContent = "No more hints";
          toast("That's all CONTROL has, Agent.", "warn");
          return;
        }
        // The progress snapshot re-renders the card with the new hint.
        hintBtn.disabled = false;
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Hint unavailable.", "err");
        hintBtn.disabled = false;
      }
    });
    card.append(hints, el("div.row", { style: "margin:8px 0" }, hintBtn));
  }

  // ---- stage 1: discover the location ----
  if (!revealed && !claimed) {
    const placeInput = el("input.input", { placeholder: "Name the place…" }) as HTMLInputElement;
    const solveBtn = el("button.btn", {}, "Confirm location") as HTMLButtonElement;
    const buyBtn = el("button.btn.btn--ghost", {},
      `Buy exact pin (${c.revealCost ?? c.powerTier * 150} ◆)`) as HTMLButtonElement;

    async function reveal(answer?: string) {
      solveBtn.disabled = buyBtn.disabled = true;
      try {
        const res = await revealCacheLocation({ cacheId: c.id, ...(answer ? { answer } : {}) });
        if (res.revealed) {
          toast(res.via === "intel"
            ? `Pin bought for ${res.cost} ◆. Move out.`
            : "Location confirmed. Pin is on your map.", "ok");
        } else {
          toast("Negative. That's not the place.", "err");
        }
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Reveal failed.", "err");
      } finally {
        solveBtn.disabled = buyBtn.disabled = false;
      }
    }
    solveBtn.addEventListener("click", () => {
      if (placeInput.value.trim()) void reveal(placeInput.value);
    });
    placeInput.addEventListener("keydown", (e) =>
      (e as KeyboardEvent).key === "Enter" && placeInput.value.trim() && void reveal(placeInput.value));
    buyBtn.addEventListener("click", () => void reveal());

    card.append(
      el("p.tiny.dim", {}, "Where is it? Name the place to drop the pin — or buy it with intel."),
      el("div.field", {}, placeInput),
      solveBtn,
      el("div", { style: "height:8px" }),
      buyBtn,
    );
  }

  // ---- stage 2: claim on site with the code + selfie ----
  if (revealed && !claimed) {
    const codeInput = el("input.input", {
      placeholder: isSupply ? "Code written on the stash…" : "Code written on the gun…",
    }) as HTMLInputElement;
    const fileInput = el("input", { type: "file", accept: "image/*", capture: "user" }) as HTMLInputElement;
    const claimBtn = el("button.btn", {},
      isSupply ? "Claim supplies (code + selfie)" : "Claim cache (code + selfie)") as HTMLButtonElement;

    claimBtn.addEventListener("click", async () => {
      if (!codeInput.value.trim()) { toast("Type the code from the drop.", "warn"); return; }
      const file = fileInput.files?.[0];
      if (!file) { toast("Claim selfie required, Agent. Strike a pose.", "warn"); return; }
      claimBtn.disabled = true;
      claimBtn.textContent = "Transmitting…";
      try {
        const blob = await noirProof(await compressImage(file));
        const { path, url } = await uploadSelfie(ctx.uid, blob);
        const res = await submitCacheAnswer({
          cacheId: c.id, answer: codeInput.value, photoPath: path, photoURL: url,
        });
        if (res.claimed) {
          // File the selfie in the surveillance feed too.
          try {
            await addDoc(collection(db, "photos"), {
              guestId: ctx.uid, codename: ctx.codename(), storagePath: path, downloadURL: url,
              caption: `CACHE CLAIMED — ${c.name}${res.first ? " · FIRST" : ""}`,
              type: "cacheClaim", cacheId: c.id, createdAt: serverTimestamp(),
            });
          } catch { /* feed entry is best-effort */ }
          toast(
            isSupply
              ? (res.first ? "🏆 FIRST TO THE STASH. Rations secured." : "Supplies secured. Share or don't.")
              : (res.first ? "🏆 FIRST CLAIM. Arm up." : "Cache claimed. Arm up."),
            "ok",
          );
        } else if (res.correct === false) {
          toast("Negative. Wrong code.", "err");
        }
      } catch (e: unknown) {
        toast((e as { message?: string })?.message ?? "Claim failed.", "err");
      } finally {
        claimBtn.disabled = false;
        claimBtn.textContent = isSupply ? "Claim supplies (code + selfie)" : "Claim cache (code + selfie)";
      }
    });

    const locParts: HTMLElement[] = [
      el("p.tiny.amber", {}, isSupply
        ? "📍 Pin on your map. Get there, find the stash, read its code."
        : "📍 Pin on your map. Get there, find the gun, read its code."),
    ];
    if (c.locationPhotoUrl) {
      locParts.push(
        el("div", { style: "margin:8px 0 4px" },
          el("span.tiny.dim", {}, "LOCATION INTEL"),
          el("img", {
            src: c.locationPhotoUrl,
            style: "display:block;width:100%;border-radius:6px;margin-top:4px;border:1px solid var(--amber);filter:grayscale(0.6) contrast(1.1) sepia(0.15)",
          }),
        ),
      );
    }
    locParts.push(
      el("div.field", {}, codeInput),
      el("div.field", {}, fileInput),
      claimBtn,
    );
    card.append(...locParts);
  }

  // ---- stage 3: claimed ----
  if (claimed) {
    card.append(el("span.stamp.stamp--green", {}, "Claimed"));
    if (c.firstClaimedBy === ctx.uid) {
      card.append(el("div.amber", { style: "margin-top:8px" }, "🏆 FIRST TO CLAIM"));
    }
    const myPhoto = c.claimPhotos?.[ctx.uid];
    if (myPhoto?.startsWith("http")) {
      card.append(el("img", {
        src: myPhoto,
        style: "width:96px;aspect-ratio:1;object-fit:cover;border-radius:6px;margin-top:8px;border:1px solid var(--green)",
      }));
    }
  }

  const finderCount = c.finders?.length ?? 0;
  if (finderCount > 0 && !claimed) {
    card.append(el("p.tiny.dim", { style: "margin-top:8px" },
      `Claimed by ${finderCount} agent${finderCount > 1 ? "s" : ""} — the spot is on everyone's map.`));
  }
  return card;
}
